import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import get from 'lodash/get';
import pick from 'lodash/pick';
import mapValues from 'lodash/mapValues';
import keyBy from 'lodash/keyBy';
import mapKeys from 'lodash/mapKeys';
import invert from 'lodash/invert';
import { EntitySchema, PseudoEntityArraySchema } from 'lib/schema';
import { formLogicRuleSchema } from '../formLogicRule';
import { camelizeNodeTypeByTicketType, decamelizeNodeTypeByTicketType } from '../form/utils';

// TODO: Make form node option an entity

// Constants
export const NODE_TYPE_ROOT = 'root';
export const NODE_TYPE_GROUP = 'group';
export const NODE_TYPE_QUESTION = 'question';
export const NODE_TYPE_QUESTION_MULTIPLE = 'question_multiple';
export const NODE_TYPE_CONTENT = 'content';
export const NODE_TYPE_TICKET_SELECTION = 'ticketSelectionNode';
export const NODE_TYPE_TICKET_SELECTION_DECAMELIZED = 'ticket_selection_node';

export const ENTITIES_KEY_NODE = 'formNodes';
export const ENTITIES_FORMS = 'forms';

export const OVERRIDE_FORM_NODE = 'entities/OVERRIDE_FORM_NODE';

export const SET_RESPONSE_AVAILABILITY = 'entities/SET_RESPONSE_AVAILABILITY';

// -- Question Nodes
export const FORM_QUESTION_DEFAULTS = {
    id: '',
    formId: null,
    type: null,
    parent: null,
    children: [],
    name: '',
    label: '',
    code: '',
    labelMarkup: '',
    inputType: 'text',
    placeholder: '',
    description: '',
    helperText: '',
    logic: [],
    config: {},
    urlParamName: null,
    crossGroupId: null, // Common id across additional guests
    lock: 0,
    personalizationTag: null,
};
export const FORM_QUESTION_NODE_KEYS = Object.keys(FORM_QUESTION_DEFAULTS);

// -- Question Multiple Nodes
export const FORM_QUESTION_MULTIPLE_DEFAULTS = {
    id: '',
    formId: null,
    type: null,
    parent: null,
    children: [],
    name: '',
    label: '',
    code: '',
    labelMarkup: '',
    inputType: 'text',
    placeholder: '',
    description: '',
    helperText: '',
    logic: [],
    config: {},
    urlParamName: null,
    lock: 0,
    fields: {},
};
export const FORM_QUESTION_MULTIPLE_NODE_KEYS = Object.keys(FORM_QUESTION_MULTIPLE_DEFAULTS);

// -- Group Nodes
export const FORM_GROUP_DEFAULTS = {
    id: '',
    formId: null,
    type: null,
    parent: null,
    children: [],
    name: '',
    label: '',
    description: '',
    logic: [],
    lock: 0,
};
export const FORM_GROUP_NODE_KEYS = Object.keys(FORM_GROUP_DEFAULTS);

// -- Content nodes
export const FORM_CONTENT_DEFAULTS = {
    id: '',
    formId: null,
    type: null,
    parent: null,
    children: [],
    name: '',
    description: '',
    contentType: 'text',
    logic: [],
    crossGroupId: null, // Common id across additional guests
    lock: 0,
};
export const FORM_CONTENT_NODE_KEYS = Object.keys(FORM_CONTENT_DEFAULTS);

// -- Ticket nodes
export const TICKET_SELECTION_NODE_DEFAULTS = {
    id: '',
    type: null,
    name: '',
    label: '',
    description: '',
    children: [],
};
export const TICKET_SELECTION_NODE_KEYS = Object.keys(TICKET_SELECTION_NODE_DEFAULTS);

// -- Root nodes
export const FORM_ROOT_DEFAULTS = {
    id: '',
    formId: null,
    type: null,
    parent: null,
    children: [],
    name: '',
    label: '',
    logic: [],
};
export const FORM_ROOT_KEYS = Object.keys(FORM_ROOT_DEFAULTS);

// ----

const KEYS_TO_MAP_WHEN_UNSERIALIZING = {
    help: 'helperText',
    personalization_tag: 'personalizationTag',
};
const KEYS_TO_MAP_WHEN_SERIALIZING = invert(KEYS_TO_MAP_WHEN_UNSERIALIZING);

// ----
// Processors
const processFormNodeEntity = (formNode, parentNode) => {
    const type = camelizeNodeTypeByTicketType(formNode.type);

    const defaultsByType = {
        [NODE_TYPE_ROOT]: FORM_ROOT_DEFAULTS,
        [NODE_TYPE_GROUP]: FORM_GROUP_DEFAULTS,
        [NODE_TYPE_QUESTION]: FORM_QUESTION_DEFAULTS,
        [NODE_TYPE_QUESTION_MULTIPLE]: FORM_QUESTION_MULTIPLE_DEFAULTS,
        [NODE_TYPE_CONTENT]: FORM_CONTENT_DEFAULTS,
        [NODE_TYPE_TICKET_SELECTION]: TICKET_SELECTION_NODE_DEFAULTS,
    };

    const defaults = cloneDeep(defaultsByType[type]);

    const mappedFormNode = mapKeys(formNode, (value, key) => KEYS_TO_MAP_WHEN_UNSERIALIZING[key] || key);

    const allowedKeysByType = {
        [NODE_TYPE_ROOT]: FORM_ROOT_KEYS,
        [NODE_TYPE_GROUP]: FORM_GROUP_NODE_KEYS,
        [NODE_TYPE_QUESTION]: FORM_QUESTION_NODE_KEYS,
        [NODE_TYPE_QUESTION_MULTIPLE]: FORM_QUESTION_MULTIPLE_NODE_KEYS,
        [NODE_TYPE_CONTENT]: FORM_CONTENT_NODE_KEYS,
        [NODE_TYPE_TICKET_SELECTION]: TICKET_SELECTION_NODE_KEYS,
    };
    const filteredNode = pick(mappedFormNode, allowedKeysByType[type]);
    const mappedValuesNode = mapValues(filteredNode, (value) =>
        value === NODE_TYPE_TICKET_SELECTION_DECAMELIZED ? camelizeNodeTypeByTicketType(value) : value,
    );

    const formId =
        formNode.type === NODE_TYPE_ROOT && parseInt(formNode.id, 10) !== parseInt(parentNode.id, 10)
            ? get(parentNode, 'id', null)
            : get(parentNode, 'formId', null);

    const parent =
        formNode.type !== NODE_TYPE_ROOT && parseInt(formNode.id, 10) !== parseInt(parentNode.id, 10)
            ? get(parentNode, 'id', formNode.parent || null)
            : null;

    return {
        ...defaults,
        ...mappedValuesNode,
        formId,
        parent,
    };
};

// Schemas

// @TODO, need to make bootstrap data mockable. At the moment, it's not. TODO
const unprocessFormNodeEntity = (formNode) => {
    const keysToExclude = ['parent', 'formId'];
    const serializeKeysByNodeType = {
        [NODE_TYPE_ROOT]: difference(FORM_ROOT_KEYS, keysToExclude),
        [NODE_TYPE_GROUP]: difference(FORM_GROUP_NODE_KEYS, keysToExclude),
        [NODE_TYPE_QUESTION]: difference(FORM_QUESTION_NODE_KEYS, keysToExclude),
        [NODE_TYPE_QUESTION_MULTIPLE]: difference(FORM_QUESTION_MULTIPLE_NODE_KEYS, keysToExclude),
        [NODE_TYPE_CONTENT]: difference(FORM_CONTENT_NODE_KEYS, keysToExclude),
        [NODE_TYPE_TICKET_SELECTION]: difference(TICKET_SELECTION_NODE_KEYS, keysToExclude),
    };
    const serializeKeys = serializeKeysByNodeType[formNode.type];

    const filteredFormNode = pick(formNode, serializeKeys);

    const mappedFormNode = mapKeys(filteredFormNode, (value, key) => KEYS_TO_MAP_WHEN_SERIALIZING[key] || key);
    const mappedValuesFormNode = mapValues(mappedFormNode, (value) =>
        value === NODE_TYPE_TICKET_SELECTION ? decamelizeNodeTypeByTicketType(value) : value,
    );

    return mappedValuesFormNode;
};

export const formRootNodeSchema = new EntitySchema(
    ENTITIES_KEY_NODE,
    {},
    {
        processStrategy: processFormNodeEntity,
        unprocessStrategy: unprocessFormNodeEntity,
    },
);

export const formNodeSchema = new EntitySchema(
    ENTITIES_KEY_NODE,
    {},
    {
        processStrategy: processFormNodeEntity,
        unprocessStrategy: unprocessFormNodeEntity,
    },
);

export const formOptionsSchema = new PseudoEntityArraySchema(
    {},
    {
        includeIdNormalized: true,
        includeIdSerialized: false,
        generateId: true,
        idAttribute: 'id',
    },
);

// Schema Relations
formRootNodeSchema.define({
    children: [formNodeSchema],
    logic: [formLogicRuleSchema],
});

formNodeSchema.define({
    children: [formNodeSchema],
    logic: [formLogicRuleSchema],
    config: {
        options: formOptionsSchema,
    },
});

// Actions
export const overrideFormNode = (newNode) => ({
    type: OVERRIDE_FORM_NODE,
    payload: {
        entities: {
            [ENTITIES_KEY_NODE]: {
                [newNode.id]: newNode,
            },
        },
    },
});

// Thunks
export const setNodeAvailability = (response) => (dispatch, getState) => {
    const state = getState();
    const nodes = state.entities[ENTITIES_KEY_NODE];
    const updatedNodes = mapValues(response, (descriptor, nodeId) => {
        const previousNode = nodes[nodeId];
        const optionsByValue = keyBy(descriptor, 'value');

        const previousOptionEntities = previousNode.config.options.entities;
        const newOptionEntities = mapValues(previousOptionEntities, (option) => {
            const available = get(optionsByValue, [option.value, 'available']);
            return {
                ...option,
                available,
                selected: Boolean(option.selected),
            };
        });

        return {
            ...previousNode,
            config: {
                ...previousNode.config,
                options: {
                    ...previousNode.config.options,
                    entities: newOptionEntities,
                },
            },
        };
    });

    dispatch({
        type: SET_RESPONSE_AVAILABILITY,
        payload: {
            entities: {
                [ENTITIES_KEY_NODE]: {
                    ...updatedNodes,
                },
            },
        },
    });
};
